import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRTL = language === 'ar';
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('رابط غير صالح', 'Invalid link — no token provided', 'Lien invalide'));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError(t('كلمة المرور يجب أن تكون 6 أحرف على الأقل', 'Password must be at least 6 characters', 'Min. 6 caractères'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('كلمتا المرور غير متطابقتين', 'Passwords do not match', 'Les mots de passe ne correspondent pas'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || t('فشل إعادة تعيين كلمة المرور', 'Failed to reset password', 'Échec'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Card className="w-full max-w-md p-8" data-testid="reset-password-card">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#2c3e50] mb-2">
            {t('إعادة تعيين كلمة المرور', 'Reset Password', 'Réinitialiser')}
          </h1>
          <p className="text-gray-600 text-sm">
            {t('أدخل كلمة المرور الجديدة', 'Enter your new password', 'Entrez le nouveau mot de passe')}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="reset-error-alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success ? (
          <Alert className="mb-4 bg-green-50 border-green-200" data-testid="reset-success-alert">
            <AlertDescription className="text-green-800">
              {t('تم إعادة تعيين كلمة المرور بنجاح! جاري التوجيه إلى تسجيل الدخول...',
                'Password reset successful! Redirecting to login...',
                'Mot de passe réinitialisé ! Redirection...')}
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
            <div>
              <Label htmlFor="password">{t('كلمة المرور الجديدة', 'New Password', 'Nouveau mot de passe')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('6 أحرف على الأقل', 'At least 6 characters', 'Min. 6 caractères')}
                required
                minLength={6}
                data-testid="new-password-input"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="password_confirm">{t('تأكيد كلمة المرور', 'Confirm Password', 'Confirmer')}</Label>
              <Input
                id="password_confirm"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder={t('أعد إدخال كلمة المرور', 'Re-enter your password', 'Ressaisissez')}
                required
                minLength={6}
                data-testid="new-password-confirm-input"
                className="mt-2"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2]"
              data-testid="reset-submit-button"
            >
              {loading ? t('جاري الحفظ...', 'Saving...', 'Enregistrement...') : t('إعادة تعيين', 'Reset Password', 'Réinitialiser')}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-[#1e88e5] hover:underline" data-testid="back-to-login-link">
            {t('العودة إلى تسجيل الدخول', 'Back to login', 'Retour à la connexion')}
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
